/**
 * /sales · High-conversion sales page for consultants.
 *
 * Reuses the design tokens + Shadcn primitives + PricingSection + existing
 * lucide-react icons — zero new libraries or components introduced.
 * Structure: Hero → Problem → Growth System → Capabilities → Why →
 * Pricing → FAQ → Final CTA.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import PricingSection from "../components/PricingSection";
import { useAuth } from "../lib/auth";
import {
  ArrowRight, PlayCircle, Sparkles, ShieldCheck, BadgeCheck, ScrollText,
  Target, Users, MessageSquare, FileText, Layers, Send, TrendingUp, Globe2,
  Zap, Link as LinkIcon, LineChart, Settings, Brain, Star, Check, Radar,
  Award, ClipboardList, Lock, PenSquare, CalendarClock,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Static content
// ---------------------------------------------------------------------------
const PROBLEMS = [
  {
    icon: Radar,
    title: "Cold outreach doesn't scale",
    body:
      "Every message goes into the void. Prospects need proof you understand their business before they'll take the call.",
  },
  {
    icon: ClipboardList,
    title: "Manual diagnostics eat your week",
    body:
      "Every proposal starts from scratch. Discovery, benchmarks, slides — hours of prep for a single conversation.",
  },
  {
    icon: MessageSquare,
    title: "Generic AI content impresses no one",
    body:
      "CFOs and COOs spot LLM prose in seconds. Hallucinated stats and vague recommendations kill your credibility.",
  },
  {
    icon: TrendingUp,
    title: "Pipeline is inconsistent",
    body:
      "Feast-or-famine cycles. Great months followed by silent ones because nothing on your website converts on autopilot.",
  },
];

const GROWTH_JOURNEY = [
  {
    step: "Attract",
    icon: Radar,
    body:
      "Publish your branded diagnostic landing on your own domain. Every visitor becomes a qualified lead touchpoint.",
  },
  {
    step: "Capture",
    icon: Users,
    body:
      "Prospects hand over contact details to unlock the executive report. Every completion enters your CRM instantly.",
  },
  {
    step: "Diagnose",
    icon: Brain,
    body:
      "15–20 executive questions auto-tailored to country, industry and function. No prompt engineering, no manual work.",
  },
  {
    step: "Impress",
    icon: Award,
    body:
      "An Executive Growth Report with cited peer benchmarks, regulatory obligations and a P0/P1 roadmap lands in their inbox.",
  },
  {
    step: "Convert",
    icon: CalendarClock,
    body:
      "Pre-filled WhatsApp + email templates + one-click booking make the discovery call a natural next step.",
  },
  {
    step: "Nurture",
    icon: Send,
    body:
      "Automated multi-touch email sequences and LinkedIn assets keep the conversation warm without extra effort.",
  },
  {
    step: "Grow",
    icon: LineChart,
    body:
      "Proposal builder + campaign kits turn every closed engagement into repeat business and referral fuel.",
  },
];

const CAPABILITIES = [
  {
    group: "Marketing",
    icon: Globe2,
    items: ["Branded landing pages", "SEO-friendly public URLs", "QR code sharing", "Multi-channel campaign links"],
  },
  {
    group: "Lead Generation",
    icon: Users,
    items: ["Qualified lead capture", "Disposable-email filter", "Consent + GDPR compliance", "Instant email notifications"],
  },
  {
    group: "AI Diagnostics",
    icon: Brain,
    items: ["Country/industry/function tailoring", "15–20 executive questions", "Category scoring", "Risk banding"],
  },
  {
    group: "Executive Reports",
    icon: FileText,
    items: ["Grounded citations [E1]…", "Regulatory obligations panel", "Confidence chip", "PDF Sources appendix"],
  },
  {
    group: "Proposal Builder",
    icon: PenSquare,
    items: ["One-click generation", "Editable templates", "Branded PDF export", "Reuse across engagements"],
  },
  {
    group: "Nurture",
    icon: Send,
    items: ["Automated email sequences", "Timing controls", "Personalised bodies", "Opt-out compliance"],
  },
  {
    group: "LinkedIn Assets",
    icon: Star,
    items: ["Post drafts per lead", "InMail templates", "Comment starters", "Hook variations"],
  },
  {
    group: "Campaigns",
    icon: Target,
    items: ["Multi-campaign management", "Per-campaign analytics", "A/B share links", "Referral tracking"],
  },
  {
    group: "Analytics",
    icon: LineChart,
    items: ["Lead funnel dashboard", "Assessment completion rate", "Report open + share stats", "Campaign attribution"],
  },
  {
    group: "Administration",
    icon: Settings,
    items: ["Team invites", "Role controls", "Billing management", "Tenant-isolated data"],
  },
];

const DIFFERENTIATORS = [
  {
    icon: BadgeCheck,
    title: "Evidence-backed reports",
    body:
      "Every percentile, delta and benchmark is cited [E<n>]. If we can't back a claim, we leave it blank — never hallucinated.",
  },
  {
    icon: ScrollText,
    title: "Regulatory intelligence",
    body:
      "ZATCA, PDPL, GDPR and GST rules fire deterministically by jurisdiction. Your reports arrive audit-ready.",
  },
  {
    icon: Lock,
    title: "White-labelled platform",
    body:
      "Your logo, brand colours and biography power the entire prospect experience. Nobody sees BizPulseAI until the report.",
  },
  {
    icon: Sparkles,
    title: "AI-generated deliverables",
    body:
      "Reports, proposals, nurture emails, LinkedIn assets — all produced from one assessment. Ten minutes of prep, hours saved.",
  },
  {
    icon: Users,
    title: "Built for consultants",
    body:
      "Not a general AI tool retrofitted for consulting. Every workflow — capture, diagnose, propose, nurture — is designed around your funnel.",
  },
  {
    icon: ShieldCheck,
    title: "Your leads stay yours",
    body:
      "Tenant-isolated by design. Your data, your prospects, your relationships. Enforced at the database layer end-to-end.",
  },
];

const FAQ = [
  {
    q: "Who is BizPulseAI built for?",
    a: "Independent consultants and boutique consulting firms — Growth, Finance, Ops, HR, Strategy — who want a repeatable engine for turning visitors into qualified conversations.",
  },
  {
    q: "How is this different from ChatGPT plus a landing-page builder?",
    a: "Every numeric claim in a BizPulseAI report is backed by a verifiable peer citation. Regulatory obligations are deterministic rules, not AI prose. And every lead + report lives inside a tenant-isolated workspace with its own audit trail — none of which you get from a stack of point tools.",
  },
  {
    q: "Do I need to build the assessment myself?",
    a: "No. You pick the country, industry and function you sell into. BizPulseAI auto-generates the 15–20 executive-level questions. Set-up takes under 10 minutes.",
  },
  {
    q: "Can I white-label the prospect experience?",
    a: "Yes — your logo, brand colours, biography and booking link power the entire prospect-facing surface. Prospects only see the BizPulseAI brand once they receive their report.",
  },
  {
    q: "What countries and industries are covered?",
    a: "20+ countries across MENA, EU, APAC and the Americas with localised peer benchmarks. Industries span Financial Services, Manufacturing, Retail, Tech, Professional Services and more — the diagnostic auto-tailors to your pick.",
  },
  {
    q: "How does billing work?",
    a: "Monthly or lifetime. Cancel any time inside the first 14 days for a full refund. Plan upgrades and downgrades are prorated automatically.",
  },
  {
    q: "What is the Enterprise plan?",
    a: "Enterprise is a premium, guided onboarding experience for consulting firms — not automated self-serve. Our team partners with you to set up your branded Consultant Platform, white-label your reports, spin up team workspaces and tune your brand kit. You also get priority onboarding and dedicated support so your practice launches under your brand, end-to-end.",
  },
  {
    q: "Can I export my data if I leave?",
    a: "Every lead, report and campaign is exportable as JSON + PDF from your dashboard. Your data belongs to you.",
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function Sales() {
  const nav = useNavigate();
  const { user } = useAuth();
  const primaryLabel = user ? "Open dashboard" : "Start 14-day trial";
  const primaryTarget = user ? "/dashboard" : "/register";

  return (
    <div className="overflow-hidden" data-testid="sales-page">
      {/* ============================= 1 · Hero ============================= */}
      <section className="relative" data-testid="sales-hero">
        <div className="ambient-glow w-[600px] h-[600px] -top-40 -left-40" style={{ background: "hsl(34, 30%, 75%)" }} />
        <div className="ambient-glow w-[500px] h-[500px] top-20 -right-40" style={{ background: "hsl(217, 91%, 60%)" }} />
        <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-32 lg:pb-32 relative text-center">
          <div className="overline text-accent fade-up-1 justify-center inline-flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5" /> For independent consultants & boutique firms
          </div>
          <h1
            className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.05] tracking-tight fade-up-2"
            data-testid="sales-headline"
          >
            Turn your <span className="italic text-accent">expertise</span> into a<br className="hidden md:inline" />{" "}
            predictable pipeline of paying clients.
          </h1>
          <p
            className="text-lg sm:text-xl text-muted-foreground mt-6 max-w-2xl mx-auto leading-relaxed fade-up-3"
            data-testid="sales-subheadline"
          >
            BizPulseAI is the growth operating system for consultants. Attract, diagnose, impress and convert — all from one branded workspace.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3 fade-up-3">
            <Button
              size="lg" className="rounded-full px-8 h-12 text-base"
              onClick={() => nav(primaryTarget)}
              data-testid="sales-hero-primary-cta"
            >
              {primaryLabel} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              size="lg" variant="outline"
              className="rounded-full px-8 h-12 text-base"
              onClick={() => nav("/sample")}
              data-testid="sales-hero-secondary-cta"
            >
              <PlayCircle className="w-4 h-4 mr-2" /> See a live report
            </Button>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-accent" /> 14-day money back</span>
            <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-accent" /> Cancel any time</span>
            <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-accent" /> Cited claims only</span>
          </div>
        </div>
      </section>

      {/* ======================= 2 · Consultant Problem ======================= */}
      <section
        className="border-t border-border/40 bg-secondary/25 py-20 lg:py-28"
        data-testid="sales-problem"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="overline text-accent">The consultant problem</div>
            <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">
              You have the expertise. <span className="italic text-accent">Getting seen is the fight.</span>
            </h2>
            <p className="text-muted-foreground mt-4 text-lg leading-relaxed">
              Every senior consultant loses hours a week to work that doesn&apos;t compound. BizPulseAI removes the four biggest drags.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-5">
            {PROBLEMS.map((p) => (
              <Card
                key={p.title} className="card-elevated p-6 flex gap-4"
                data-testid={`sales-problem-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
              >
                <p.icon className="w-6 h-6 text-accent shrink-0" />
                <div>
                  <div className="font-sans-ui font-semibold text-lg">{p.title}</div>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{p.body}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== 3 · The BizPulseAI Growth System ==================== */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28" data-testid="sales-growth-system">
        <div className="max-w-3xl">
          <div className="overline text-accent">The BizPulseAI Growth System™</div>
          <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">
            Seven stages between{" "}
            <span className="italic text-accent">stranger</span> and{" "}
            <span className="italic text-accent">signed engagement.</span>
          </h2>
          <p className="text-muted-foreground mt-4 text-lg leading-relaxed">
            One workflow. One workspace. Every touchpoint automated except the human moments where you add value.
          </p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {GROWTH_JOURNEY.map((s, i) => (
            <Card
              key={s.step} className="card-elevated p-5 relative flex flex-col"
              data-testid={`sales-journey-${s.step.toLowerCase()}`}
            >
              <div className="absolute -top-3 -left-3 w-9 h-9 rounded-full bg-accent text-accent-foreground font-mono text-xs flex items-center justify-center shadow-lg">
                {String(i + 1).padStart(2, "0")}
              </div>
              <s.icon className="w-5 h-5 text-accent mt-3" />
              <div className="font-sans-ui font-semibold mt-3 text-base">{s.step}</div>
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed flex-1">{s.body}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* ==================== 4 · Platform Capabilities ==================== */}
      <section
        className="bg-secondary/30 border-y border-border/40 py-20 lg:py-28"
        data-testid="sales-capabilities"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="overline text-accent">Platform capabilities</div>
            <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">
              The <span className="italic text-accent">whole growth stack</span>, without the SaaS sprawl.
            </h2>
            <p className="text-muted-foreground mt-4 text-lg leading-relaxed">
              Ten purpose-built modules replace the ten point tools most consultants stitch together and pay for separately.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {CAPABILITIES.map((c) => (
              <Card
                key={c.group} className="card-elevated p-6"
                data-testid={`sales-capability-${c.group.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="flex items-center gap-3">
                  <c.icon className="w-5 h-5 text-accent" />
                  <div className="font-sans-ui font-semibold">{c.group}</div>
                </div>
                <ul className="mt-4 space-y-2">
                  {c.items.map((it) => (
                    <li key={it} className="text-sm text-muted-foreground flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-accent mt-0.5 shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* =============== 5 · Why BizPulseAI Is Different =============== */}
      <section
        className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28"
        data-testid="sales-differentiators"
      >
        <div className="max-w-3xl">
          <div className="overline text-accent">Why BizPulseAI is different</div>
          <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">
            Not another AI wrapper.{" "}
            <span className="italic text-accent">A consultant&apos;s growth engine.</span>
          </h2>
          <p className="text-muted-foreground mt-4 text-lg leading-relaxed">
            The features look similar on paper. The trust the artefact commands with your prospect is where BizPulseAI wins.
          </p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {DIFFERENTIATORS.map((d) => (
            <Card
              key={d.title} className="card-elevated p-6"
              data-testid={`sales-diff-${d.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
            >
              <d.icon className="w-6 h-6 text-accent" />
              <div className="font-sans-ui font-semibold mt-4 text-lg">{d.title}</div>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{d.body}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* ============================ 6 · Pricing ============================ */}
      <div id="pricing" data-testid="sales-pricing-anchor">
        <PricingSection
          eyebrow="Pricing"
          heading={
            <>
              Pays for itself with{" "}
              <span className="italic text-accent">one signed engagement.</span>
            </>
          }
          subheading="Monthly or lifetime. 14-day money back. Cancel any time."
        />
      </div>

      {/* ================================ 7 · FAQ ================================ */}
      <section
        className="max-w-4xl mx-auto px-6 lg:px-10 py-20 lg:py-28"
        data-testid="sales-faq" id="faq"
      >
        <div className="text-center">
          <div className="overline text-accent">Common questions</div>
          <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">
            Everything you&apos;d ask on a <span className="italic text-accent">sales call.</span>
          </h2>
        </div>
        <Accordion type="single" collapsible className="mt-10">
          {FAQ.map((item, i) => (
            <AccordionItem key={item.q} value={`sales-faq-${i}`} data-testid={`sales-faq-${i + 1}`}>
              <AccordionTrigger className="text-left text-base font-medium">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* ============================ 8 · Final CTA ============================ */}
      <section
        className="relative overflow-hidden border-y border-border/40 bg-secondary/30"
        data-testid="sales-final-cta"
      >
        <div className="ambient-glow w-[500px] h-[500px] -top-40 -right-40" style={{ background: "hsl(34, 30%, 75%)" }} />
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 lg:py-28 text-center relative">
          <div className="overline text-accent">Ready when you are</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight">
            Ship the pipeline. <span className="italic text-accent">Skip the busywork.</span>
          </h2>
          <p className="text-muted-foreground mt-6 text-lg max-w-2xl mx-auto leading-relaxed">
            Your first branded diagnostic is 10 minutes away. Cancel any time inside the first 14 days for a full refund.          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Button
              size="lg" className="rounded-full px-8 h-12 text-base"
              onClick={() => nav(primaryTarget)}
              data-testid="sales-final-primary-cta"
            >
              {primaryLabel} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              size="lg" variant="outline"
              className="rounded-full px-8 h-12 text-base"
              onClick={() => nav("/pricing")}
              data-testid="sales-final-secondary-cta"
            >
              <LinkIcon className="w-4 h-4 mr-2" /> Compare plans
            </Button>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground uppercase tracking-widest">
            <span className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> Tenant-isolated</span>
            <span className="flex items-center gap-2"><Zap className="w-3.5 h-3.5" /> 10-min setup</span>
            <span className="flex items-center gap-2"><BadgeCheck className="w-3.5 h-3.5" /> Cited claims only</span>
            <span className="flex items-center gap-2"><Layers className="w-3.5 h-3.5" /> Full growth stack</span>
          </div>
        </div>
      </section>
    </div>
  );
}
